import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  alpha,
  useTheme,
  IconButton,
  Stack,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CakeIcon from '@mui/icons-material/Cake';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import dayjs from 'dayjs';

interface Person {
  id: string;
  name: string;
  preferredMethod?: string;
  email?: string;
  phone?: string;
  birthday?: string;
  city?: string;
  country?: string;
  notes?: string;
}

interface PersonCardProps {
  person: Person;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

interface RowProps {
  icon: React.ReactNode;
  text: string;
  theme: any;
}

const Row: React.FC<RowProps> = ({ icon, text, theme }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        bgcolor: alpha(theme.palette.primary.main, 0.1),
        color: 'primary.main',
      }}
    >
      {icon}
    </Box>
    <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
      {text}
    </Typography>
  </Box>
);

const PersonCard: React.FC<PersonCardProps> = ({ person, onEdit, onDelete }) => {
  const theme = useTheme();
  const location = [person.city, person.country].filter(Boolean).join(', ');
  const initials = person.name
    .split(' ')
    .map((part) => part.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: theme.shadows[4] },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        },
      }}
      onClick={() => onEdit(person.id)}
      role="button"
      aria-label={`Edit ${person.name}`}
    >
      <CardContent sx={{ p: 2, pb: 1, flexGrow: 1, pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 46,
              height: 46,
              fontSize: '1.05rem',
              fontWeight: 'bold',
              color: 'primary.contrastText',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              flexShrink: 0,
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
              {person.name}
            </Typography>
            {person.preferredMethod ? (
              <Chip
                label={`Prefers ${person.preferredMethod}`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.6rem', mt: 0.5 }}
              />
            ) : location ? (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.25 }}>
                {location}
              </Typography>
            ) : null}
          </Box>

          <Box
            sx={{ display: 'flex', gap: 0.25, opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.2s' }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton size="small" onClick={() => onEdit(person.id)} aria-label="Edit" sx={{ color: 'text.secondary' }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => onDelete(person.id)} aria-label="Delete">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Stack spacing={1} sx={{ mt: 1.5 }}>
          {person.email && <Row theme={theme} icon={<EmailIcon sx={{ fontSize: 14 }} />} text={person.email} />}
          {person.phone && <Row theme={theme} icon={<PhoneIcon sx={{ fontSize: 14 }} />} text={person.phone} />}
          {!person.email && !person.phone && location && (
            <Row theme={theme} icon={<LocationOnIcon sx={{ fontSize: 14 }} />} text={location} />
          )}
          {person.birthday && (
            <Row theme={theme} icon={<CakeIcon sx={{ fontSize: 14 }} />} text={dayjs(person.birthday).format('D MMM YYYY')} />
          )}
        </Stack>

        {person.notes && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontStyle: 'italic',
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.15),
              borderRadius: 1.5,
              p: 1,
              lineHeight: 1.4,
            }}
          >
            {person.notes}
          </Typography>
        )}
      </CardContent>

      <Box
        sx={{
          px: 2,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.background.paper, 0.4),
        }}
      >
        {location ? (
          <Typography variant="caption" color="text.secondary" noWrap>
            <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', mr: 0.5, color: 'primary.main' }}>
              <LocationOnIcon sx={{ fontSize: 13 }} />
            </Box>
            {location}
          </Typography>
        ) : (
          <span />
        )}
        <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'capitalize' }}>
          {person.preferredMethod ? `via ${person.preferredMethod}` : 'Contact'}
        </Typography>
      </Box>
    </Card>
  );
};

export default PersonCard;
